import { useEffect, useMemo, useRef, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { FaArrowLeft, FaMicrophone, FaPaperPlane, FaRobot, FaStar, FaMagic, FaUser, FaLightbulb, FaHistory } from 'react-icons/fa';
import { FaMapMarkerAlt } from 'react-icons/fa';
import bookingAssistantAPI from '../services/bookingAssistantAPI';
import { getPropertyImage } from '../utils/imageHelper';
import { formatPrice } from '../utils/currencyPreference';
import Navbar from '../components/Navbar';
import { SmartMatchBadge } from '../components/SmartMatchBadge';
import { scorePropertyMatch } from '../utils/smartMatchScore';
import './BookingAssistantPage.css';
import '../components/SmartMatchBadge.css';

const WELCOME_BY_LANG = {
  en: "Hi! Tell me about your trip or meeting — I'll find the perfect stay or meeting room for you.",
  kn: 'ನಮಸ್ಕಾರ! ನಿಮ್ಮ ಪ್ರಯಾಣ ಅಥವಾ ಮೀಟಿಂಗ್ ಬಗ್ಗೆ ಹೇಳಿ — ನಾನು ಸರಿಯಾದ ಸ್ಟೇ ಅಥವಾ ಮೀಟಿಂಗ್ ರೂಮ್ ಹುಡುಕಿ ಕೊಡುತ್ತೇನೆ.',
  hi: 'नमस्ते! अपनी यात्रा या मीटिंग के बारे में बताएं — मैं सही स्टे या मीटिंग रूम ढूंढूंगा।',
  ml: 'ഹായ്! നിങ്ങളുടെ യാത്രയോ മീറ്റിംഗോ പറയൂ — ഞാൻ ശരിയായ സ്റ്റേയോ മീറ്റിംഗ് റൂമോ കണ്ടെത്താം.',
};

const SUGGESTED_PROMPTS = [
  'Family-friendly stay near the beach under ₹5,000',
  'Quiet workcation with high-speed Wi-Fi and kitchen',
  'Meeting room in Mangaluru for 10 people',
  'Conference room in Kadaba under ₹2,000/hour',
  'Business hotel close to the airport with breakfast included',
];

const SUGGESTED_PROMPTS_KN = [
  'ನನಗೆ ಕಡಬದಲ್ಲಿ ಇರುವ ರೂಮ್ಸ್ ಶೋ ಮಾಡಿ',
  'Mangaluru nalli 10 janarige meeting room',
  'Kadaba alli meeting room show madi',
  'Wi-Fi iruva workcation stay',
];

const LANGUAGES = [
  { code: 'en', label: 'English' },
  { code: 'kn', label: 'ಕನ್ನಡ' },
  { code: 'hi', label: 'हिंदी' },
  { code: 'ml', label: 'മലയാളം' },
];

const BookingAssistantPage = () => {
  const navigate = useNavigate();
  const welcomeMessage = WELCOME_BY_LANG.en;
  const [messages, setMessages] = useState([{ role: 'assistant', content: welcomeMessage, recommendations: [] }]);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [language, setLanguage] = useState('en');
  const [sessionId, setSessionId] = useState(null);
  const [listening, setListening] = useState(false);
  const [savedSessions, setSavedSessions] = useState([]);
  const bottomRef = useRef(null);
  const recognitionRef = useRef(null);

  const history = useMemo(
    () => messages.filter((m) => m.role === 'user' || m.role === 'assistant').map((m) => ({ role: m.role, content: m.content })),
    [messages]
  );

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages, loading]);

  useEffect(() => {
    const welcome = WELCOME_BY_LANG[language] || WELCOME_BY_LANG.en;
    setMessages((prev) => {
      if (prev.length === 1 && prev[0].role === 'assistant' && !prev[0].intent) {
        return [{ ...prev[0], content: welcome }];
      }
      return prev;
    });
  }, [language]);

  useEffect(() => {
    const token = localStorage.getItem('token');
    if (!token) return;
    bookingAssistantAPI
      .getSessions()
      .then((res) => setSavedSessions(res.data.sessions || []))
      .catch(() => {});
  }, []);

  useEffect(() => {
    const SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition;
    if (!SpeechRecognition) return undefined;

    const recognition = new SpeechRecognition();
    recognition.continuous = false;
    recognition.interimResults = false;
    recognition.lang = language === 'kn' ? 'kn-IN' : language === 'hi' ? 'hi-IN' : language === 'ml' ? 'ml-IN' : 'en-IN';

    recognition.onresult = (event) => {
      const transcript = event.results?.[0]?.[0]?.transcript || '';
      if (transcript) setInput((prev) => (prev ? `${prev} ${transcript}` : transcript));
      setListening(false);
    };
    recognition.onerror = () => setListening(false);
    recognition.onend = () => setListening(false);
    recognitionRef.current = recognition;

    return () => {
      recognition.stop();
    };
  }, [language]);

  const sendMessage = async (text, { surpriseMe = false } = {}) => {
    const trimmed = String(text || '').trim();
    if (!trimmed || loading) return;

    setMessages((prev) => [...prev, { role: 'user', content: trimmed }]);
    setInput('');
    setLoading(true);

    try {
      const res = await bookingAssistantAPI.chat({
        message: trimmed,
        history: history.slice(-10),
        sessionId,
        language,
        surpriseMe,
        guestKey: bookingAssistantAPI.guestKey(),
      });

      const { reply, recommendations = [], sessionId: sid } = res.data;
      if (sid) setSessionId(sid);

      setMessages((prev) => [
        ...prev,
        { role: 'assistant', content: reply, recommendations, intent: res.data.intent },
      ]);

      localStorage.setItem(
        'bookingAssistantLastSession',
        JSON.stringify({ sessionId: sid || sessionId, updatedAt: Date.now() })
      );
    } catch (err) {
      setMessages((prev) => [
        ...prev,
        {
          role: 'assistant',
          content:
            err.response?.data?.message ||
            'Sorry, I had trouble processing that. Please try again.',
          recommendations: [],
        },
      ]);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    sendMessage(input);
  };

  const toggleVoice = () => {
    const recognition = recognitionRef.current;
    if (!recognition) {
      alert('Voice search is not supported in this browser.');
      return;
    }
    if (listening) {
      recognition.stop();
      setListening(false);
      return;
    }
    setListening(true);
    recognition.start();
  };

  const handleBook = (property, intent) => {
    if (property.bookingType === 'meeting') {
      if (property.isExternal && property.externalLink) {
        window.open(property.externalLink, '_blank', 'noopener,noreferrer');
        return;
      }
      const isDbRoom = /^[a-f\d]{24}$/i.test(String(property.id || ''));
      if (isDbRoom) {
        navigate(`/meetings/room/${property.id}`);
        return;
      }
      navigate(`/meetings/search-results?location=${encodeURIComponent(property.city || intent?.destination || '')}`);
      return;
    }
    const params = new URLSearchParams();
    if (intent?.checkIn) params.set('checkIn', intent.checkIn);
    if (intent?.checkOut) params.set('checkOut', intent.checkOut);
    if (intent?.guests?.adults) params.set('adults', String(intent.guests.adults));
    const qs = params.toString();
    navigate(`/property/${property.id}${qs ? `?${qs}` : ''}`);
  };

  const loadSession = async (id) => {
    try {
      const res = await bookingAssistantAPI.getSession(id);
      const session = res.data.session;
      if (!session?.messages?.length) return;
      setSessionId(session._id);
      setLanguage(session.language || 'en');
      setMessages(
        session.messages.map((m) => ({
          role: m.role,
          content: m.content,
          recommendations: m.recommendations || [],
          intent: m.intent,
        }))
      );
    } catch {
      /* ignore */
    }
  };

  return (
    <div className="ai-assistant-page">
      <Navbar />
      <header className="ai-assistant-header">
        <div className="ai-assistant-brand">
          <span className="ai-assistant-brand-icon">
            <FaRobot />
          </span>
          <div>
            <h1>UBSA AI Booking Assistant</h1>
            <p>Don&apos;t search — describe your trip</p>
          </div>
        </div>
        <div className="ai-assistant-header-actions">
          <select
            className="ai-assistant-lang"
            value={language}
            onChange={(e) => setLanguage(e.target.value)}
            aria-label="Conversation language"
          >
            {LANGUAGES.map((l) => (
              <option key={l.code} value={l.code}>
                {l.label}
              </option>
            ))}
          </select>
          <Link to="/" className="ai-assistant-home-link">
            <FaArrowLeft />
            <span className="ai-assistant-home-label">Home</span>
          </Link>
        </div>
      </header>

      <div className="ai-assistant-main">
        <aside className="ai-assistant-sidebar">
          <div className="ai-sidebar-section ai-sidebar-section--prompts">
            <h3>
              <FaLightbulb />
              Try asking
            </h3>
            <div className="ai-assistant-chips ai-assistant-chips--prompts">
              {(language === 'kn' ? SUGGESTED_PROMPTS_KN : SUGGESTED_PROMPTS).map((prompt) => (
                <button
                  key={prompt}
                  type="button"
                  className="ai-assistant-chip"
                  onClick={() => sendMessage(prompt)}
                  disabled={loading}
                >
                  {prompt}
                </button>
              ))}
            </div>
          </div>

          <div className="ai-sidebar-actions">
            <button
              type="button"
              className="ai-assistant-surprise"
              onClick={() => sendMessage('Surprise me with a unique hidden gem stay in India', { surpriseMe: true })}
              disabled={loading}
            >
              <span className="ai-surprise-icon">
                <FaMagic />
              </span>
              <span className="ai-surprise-text">
                <strong>Surprise Me</strong>
                <small>Discover a hidden gem</small>
              </span>
            </button>
          </div>

          {savedSessions.length > 0 && (
            <div className="ai-sidebar-section ai-sidebar-section--saved">
              <h3>
                <FaHistory />
                Saved chats
              </h3>
              <div className="ai-assistant-chips ai-assistant-chips--saved">
                {savedSessions.slice(0, 5).map((s) => (
                  <button
                    key={s.id}
                    type="button"
                    className="ai-assistant-chip ai-assistant-chip--saved"
                    onClick={() => loadSession(s.id)}
                    title={s.title || 'Trip planning'}
                  >
                    <FaHistory className="ai-chip-icon" />
                    <span className="ai-chip-label">{s.title || 'Trip planning'}</span>
                  </button>
                ))}
              </div>
            </div>
          )}
        </aside>

        <section className="ai-assistant-chat">
          <div className={`ai-assistant-messages${messages.length === 1 ? ' ai-assistant-messages--welcome' : ''}`}>
            {messages.length === 1 && messages[0].role === 'assistant' && (
              <div className="ai-welcome-hero">
                <div className="ai-welcome-icon">
                  <FaRobot />
                </div>
                <h2>Your personal travel concierge</h2>
                <p>{WELCOME_BY_LANG[language] || WELCOME_BY_LANG.en}</p>
              </div>
            )}
            {messages.map((msg, idx) => {
              const isInitialWelcome =
                messages.length === 1 && idx === 0 && msg.role === 'assistant';
              if (isInitialWelcome) return null;

              return (
              <div key={`${msg.role}-${idx}`} className={`ai-msg ai-msg-${msg.role}`}>
                <span className={`ai-msg-avatar ai-msg-avatar--${msg.role}`} aria-hidden="true">
                  {msg.role === 'assistant' ? <FaRobot /> : <FaUser />}
                </span>
                <div className="ai-msg-content">
                <div className="ai-msg-bubble">{msg.content}</div>
                {msg.recommendations?.length > 0 && (
                  <div className="ai-rec-cards">
                    {msg.recommendations.map((property) => {
                      const isMeeting = property.bookingType === 'meeting';
                      return (
                      <article key={property.id} className={`ai-rec-card${isMeeting ? ' ai-rec-card--meeting' : ''}`}>
                        <img
                          src={getPropertyImage(property)}
                          alt={property.name}
                          loading="lazy"
                        />
                        <div className="ai-rec-body">
                          <div className="ai-rec-title-row">
                            <h4>{property.name}</h4>
                            {!isMeeting && (
                              <SmartMatchBadge
                                match={
                                  property.smartMatch ||
                                  (msg.intent
                                    ? scorePropertyMatch(property, msg.intent)
                                    : null)
                                }
                                compact
                                showReasons={false}
                              />
                            )}
                            {isMeeting && (
                              <span className="ai-rec-type-badge">
                                {property.isExternal ? 'API Result' : 'Meeting Room'}
                              </span>
                            )}
                          </div>
                          <div className="ai-rec-meta">
                            <span>
                              <FaMapMarkerAlt /> {property.city}
                              {property.state ? `, ${property.state}` : ''}
                            </span>
                            {property.rating ? (
                              <span>
                                <FaStar color="#f59e0b" /> {property.rating}
                              </span>
                            ) : null}
                            {isMeeting && property.source && property.source !== 'local' ? (
                              <span className="ai-rec-source">{property.source}</span>
                            ) : null}
                            {isMeeting && property.capacity ? (
                              <span>Up to {property.capacity} people</span>
                            ) : null}
                          </div>
                          {isMeeting && property.pricePerHour ? (
                            <p className="ai-rec-price">
                              {formatPrice(property.pricePerHour, property.currency)} / hour
                            </p>
                          ) : property.pricePerNight ? (
                            <p className="ai-rec-price">
                              {formatPrice(property.pricePerNight, property.currency)} / night
                            </p>
                          ) : null}
                          <p className="ai-rec-reason">{property.reason}</p>
                          <button
                            type="button"
                            className="ai-rec-book"
                            onClick={() => handleBook(property, msg.intent)}
                          >
                            {isMeeting
                              ? (property.isExternal ? 'Open Venue' : 'View & Book')
                              : 'Book Now'}
                          </button>
                        </div>
                      </article>
                    );
                    })}
                  </div>
                )}
                </div>
              </div>
            );
            })}
            {loading && (
              <div className="ai-msg ai-msg-assistant">
                <span className="ai-msg-avatar ai-msg-avatar--assistant" aria-hidden="true">
                  <FaRobot />
                </span>
                <div className="ai-msg-content">
                <div className="ai-msg-bubble ai-assistant-typing">
                  <span />
                  <span />
                  <span />
                </div>
                </div>
              </div>
            )}
            <div ref={bottomRef} />
          </div>

          <form className="ai-assistant-composer" onSubmit={handleSubmit}>
            <div className="ai-composer-inner">
              <button
                type="button"
                className={`ai-assistant-voice ${listening ? 'listening' : ''}`}
                onClick={toggleVoice}
                aria-label="Voice input"
              >
                <FaMicrophone />
              </button>
              <textarea
                className="ai-assistant-input"
                value={input}
                onChange={(e) => setInput(e.target.value)}
                placeholder={
                  language === 'kn'
                    ? 'ನಿಮ್ಮ ಸ್ಟೇ ಅಥವಾ ಮೀಟಿಂಗ್ ರೂಮ್ ಬಗ್ಗೆ ವಿವರಿಸಿ…'
                    : language === 'hi'
                      ? 'अपनी स्टे या मीटिंग रूम के बारे में बताएं…'
                      : language === 'ml'
                        ? 'നിങ്ങളുടെ സ്റ്റേയോ മീറ്റിംഗ് റൂമോ വിവരിക്കൂ…'
                        : 'Describe your ideal stay or meeting room…'
                }
                rows={1}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' && !e.shiftKey) {
                    e.preventDefault();
                    sendMessage(input);
                  }
                }}
              />
              <button type="submit" className="ai-assistant-send" disabled={loading || !input.trim()}>
                <FaPaperPlane />
              </button>
            </div>
            <p className="ai-composer-hint">Press Enter to send · Shift+Enter for new line</p>
          </form>
        </section>
      </div>
    </div>
  );
};

export default BookingAssistantPage;
